/**
 * Инициализация UI аутентификации для работы в режиме интеграции с Bitrix.
 *
 * Используется делегирование событий:
 * один обработчик назначается на document и обрабатывает клики
 * по текущим и динамически добавленным элементам.
 */

interface SkAuthConfig {
	triggerSelector: string | string[];
	apiBasePath: string;
	autoInit: boolean;
	mode: "bitrix" | "mock";
	abAuthVariant?: "A" | "B";
	skipPhoneVerification?: boolean;
	metrikaCounterId?: number;
	smsFallbackTimeoutSeconds?: number;
	otpSmsLength?: number;
	flashcallEnabled?: boolean;
	smsEnabled?: boolean;
	ui: {
		theme: string;
		language: string;
	};
}

declare global {
	interface Window {
		SkAuthConfig?: SkAuthConfig;
		SkAuth?: {
			open: (view: string, options?: { orderId?: number | string | null }) => void;
			close: () => void;
			init: () => void;
			destroy: () => void;
		};
	}
}

type DelegatedClickHandler = (event: MouseEvent) => void;

/**
 * Текущий делегированный обработчик.
 *
 * Хранится отдельно, чтобы:
 * - не назначать обработчик несколько раз;
 * - корректно удалить его через destroySkAuth();
 * - переинициализировать модуль при изменении конфигурации.
 */
let delegatedClickHandler: DelegatedClickHandler | null = null;

/**
 * Селекторы, используемые текущим обработчиком.
 */
let activeSelectors: string[] = [];

/**
 * Флаг готовности смонтированного React-приложения и очередь отложенного открытия
 */
let isAppReady = false;
let pendingOpenRequest: { view: string; options?: { orderId?: number | string | null } } | null = null;

export function markAppReady(): void {
	isAppReady = true;
	if (pendingOpenRequest) {
		const { view, options } = pendingOpenRequest;
		pendingOpenRequest = null;
		openAuthModal(view, options);
	}
}

/**
 * Открыть модальное окно аутентификации с указанным представлением и опциями.
 *
 * @param view Тип представления:
 * 'login', 'register', 'reset-password', 'phone-auth', 'contact-details'
 * @param options Дополнительные параметры (например, { orderId: 123 })
 */
export function openAuthModal(view: string, options?: { orderId?: number | string | null }): void {
	if (!isAppReady) {
		pendingOpenRequest = { view, options };
	}
	window.dispatchEvent(
		new CustomEvent("sk-auth:open", {
			detail: { view, options },
		}),
	);
}

/**
 * Закрыть модальное окно аутентификации.
 */
export function closeAuthModal(): void {
	pendingOpenRequest = null;
	window.dispatchEvent(new CustomEvent("sk-auth:close"));
}

/**
 * Нормализовать triggerSelector в массив уникальных непустых строк.
 */
function normalizeSelectors(triggerSelector: string | string[]): string[] {
	const selectors = Array.isArray(triggerSelector) ? triggerSelector : [triggerSelector];

	return Array.from(new Set(selectors.map((selector) => selector.trim()).filter(Boolean)));
}

/**
 * Проверить CSS-селекторы.
 *
 * Неверные селекторы исключаются, чтобы один ошибочный селектор
 * не ломал весь обработчик.
 */
function validateSelectors(selectors: string[]): string[] {
	return selectors.filter((selector) => {
		try {
			document.querySelector(selector);
			return true;
		} catch (error) {
			console.error(`[SkAuth] Неверный селектор: ${selector}`, error);

			return false;
		}
	});
}

/**
 * Найти ближайший элемент, соответствующий одному из triggerSelector.
 *
 * Использование closest() позволяет корректно обрабатывать клики
 * по вложенным элементам кнопки: span, svg, path и другим.
 */
function findTriggerElement(target: EventTarget | null, selectors: string[]): Element | null {
	if (!(target instanceof Element)) {
		return null;
	}

	for (const selector of selectors) {
		const element = target.closest(selector);

		if (element) {
			return element;
		}
	}

	return null;
}

/**
 * Инициализировать делегированный обработчик кликов.
 *
 * Обработчик назначается один раз на document и работает для:
 * - элементов, существующих при загрузке страницы;
 * - элементов, добавленных через AJAX;
 * - элементов, созданных React;
 * - элементов, заменённых при повторном рендере;
 * - элементов, появившихся после открытия модального окна.
 */
export function initSkAuth(): void {
	const config = window.SkAuthConfig;

	if (!config) {
		console.warn("[SkAuth] Конфигурация не найдена. Пропускаем инициализацию.");

		return;
	}

	const normalizedSelectors = normalizeSelectors(config.triggerSelector);

	const validSelectors = validateSelectors(normalizedSelectors);

	if (validSelectors.length === 0) {
		console.warn("[SkAuth] Не найдено ни одного корректного triggerSelector.");

		return;
	}

	/*
	 * Если обработчик уже установлен с теми же селекторами,
	 * повторно ничего не назначаем.
	 */
	const selectorsHaveNotChanged = delegatedClickHandler !== null && activeSelectors.length === validSelectors.length && activeSelectors.every((selector, index) => selector === validSelectors[index]);

	if (selectorsHaveNotChanged) {
		return;
	}

	/*
	 * Если конфигурация изменилась, сначала удаляем старый обработчик.
	 */
	if (delegatedClickHandler) {
		destroySkAuth();
	}

	activeSelectors = validSelectors;

	delegatedClickHandler = (event: MouseEvent): void => {
		const triggerElement = findTriggerElement(event.target, activeSelectors);

		if (!triggerElement) {
			return;
		}

		const triggerType = triggerElement.getAttribute("data-auth-trigger")?.trim();

		if (!triggerType) {
			console.warn("[SkAuth] У элемента отсутствует значение data-auth-trigger:", triggerElement);

			return;
		}

		event.preventDefault();
		event.stopPropagation();

		openAuthModal(triggerType);
	};

	document.addEventListener("click", delegatedClickHandler);

	/*
	 * Маркер ставится на корневой элемент документа, а не на каждую кнопку.
	 * При делегировании все подходящие элементы считаются обслуживаемыми
	 * автоматически, включая те, которые появятся позднее.
	 */
	document.documentElement.setAttribute("data-sk-auth-delegation-initialized", "true");
}

/**
 * Удалить делегированный обработчик и очистить состояние.
 */
export function destroySkAuth(): void {
	if (delegatedClickHandler) {
		document.removeEventListener("click", delegatedClickHandler);

		delegatedClickHandler = null;
	}

	activeSelectors = [];

	document.documentElement.removeAttribute("data-sk-auth-delegation-initialized");
}

/**
 * Автоматическая инициализация.
 *
 * autoInit управляет только автоматическим запуском.
 * Ручной вызов window.SkAuth.init() работает независимо от autoInit.
 */
function autoInitSkAuth(): void {
	if (window.SkAuthConfig?.autoInit) {
		initSkAuth();
	}
}

if (typeof window !== "undefined") {
	/*
	 * Сначала экспортируем глобальный API, чтобы он был доступен
	 * независимо от текущего состояния DOM.
	 */
	window.SkAuth = {
		open: openAuthModal,
		close: closeAuthModal,
		init: initSkAuth,
		destroy: destroySkAuth,
	};

	if (document.readyState === "loading") {
		document.addEventListener("DOMContentLoaded", autoInitSkAuth, { once: true });
	} else {
		autoInitSkAuth();
	}
}
